import { format } from 'date-fns';
import { taskAPI, getIpeWorkspaceMode } from '@/utils/api';
import { isManagedStorageUrl } from '@/utils/imageDisplayAccess';
import { createIpeWorkflowTask } from './ipeWorkflowTaskRecord';
import { trackPendingTask } from '@/utils/workbenchHistoryBadge';
import { toIpeUserErrorMessage } from './errorHandler';
import { logIpeClientError } from '@/utils/ipeClientErrorLogger';

import { normalizeImg2ImgOutputRow } from './img2imgTaskResult';
import {
  buildSmartToolResultRecordId,
  mergePersistedImageSpaceItems,
  persistSmartToolResultsToImageSpace,
} from './persistSmartToolResultsToImageSpace';

/** 灵图/第三方 http(s) 外链，需 ingest 落 OSS 后转内部 /uploads/ 地址 */
export function isExternalGeneratedResultUrl(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) return false;
  if (isManagedStorageUrl(s)) return false;
  if (s.startsWith('http://') || s.startsWith('https://')) return true;
  return false;
}

export function resultHasExternalOutputUrls(result) {
  const outputs = Array.isArray(result?.outputs) ? result.outputs : [];
  const urls = outputs.length
    ? outputs.map((row) => row?.outputUrl || row?.url || row?.imageUrl)
    : (Array.isArray(result?.urls) ? result.urls : []);
  return urls.some((url) => isExternalGeneratedResultUrl(url));
}

function pickPersistedImageFields(item) {
  if (!item || typeof item !== 'object') return null;
  const url = String(item.url || item.src || '').trim();
  if (!url) return null;
  const viewUrl = String(
    item.viewUrl || item.storedViewUrl || item.displayUrl || item.urlDisplay || '',
  ).trim();
  return { url, viewUrl, displayUrl: viewUrl || String(item.displayUrl || item.urlDisplay || '').trim() };
}

function resolveOutputIndexForSavedItem(outputs, item, savedIndex, options = {}) {
  const id = String(item?.id || '').trim();
  if (id && options.navId && options.taskId) {
    const byId = outputs.findIndex((row, idx) => (
      buildSmartToolResultRecordId(options.navId, options.taskId, idx, row) === id
    ));
    if (byId >= 0) return byId;
  }
  const bySource = outputs.findIndex((row) => Number(row?.sourceIndex) === savedIndex);
  if (bySource >= 0) return bySource;
  const byIndex = outputs.findIndex((row) => Number(row?.index) === savedIndex);
  if (byIndex >= 0) return byIndex;
  return savedIndex < outputs.length ? savedIndex : -1;
}

/** 图片空间入库后，把持久化 canonical / viewUrl 写回 task.result.outputs */
export function mergeIngestedResultsIntoStudioSuccessResult(successResult, savedImages = [], options = {}) {
  if (!successResult || !Array.isArray(savedImages) || savedImages.length === 0) {
    return successResult;
  }
  const outputs = Array.isArray(successResult.outputs)
    ? successResult.outputs.map((row, index) => ({ ...row, index: row?.index ?? index }))
    : [];
  if (!outputs.length) return successResult;

  let changed = false;
  savedImages.forEach((item, savedIndex) => {
    const fields = pickPersistedImageFields(item);
    if (!fields) return;
    let targetIndex = resolveOutputIndexForSavedItem(outputs, item, savedIndex, options);
    if (targetIndex < 0) {
      targetIndex = outputs.length;
      outputs.push({
        index: targetIndex,
        sourceIndex: savedIndex,
        status: 'success',
      });
    }
    const prev = outputs[targetIndex] || { index: savedIndex, sourceIndex: savedIndex, status: 'success' };
    outputs[targetIndex] = normalizeImg2ImgOutputRow({
      ...prev,
      ...fields,
      outputUrl: fields.url,
      imageUrl: fields.url,
      viewUrl: fields.viewUrl || prev.viewUrl,
      displayUrl: fields.displayUrl || prev.displayUrl,
      imageSpaceId: String(item.id || prev.imageSpaceId || '').trim() || prev.imageSpaceId,
      name: String(item.name || prev.name || '').trim() || prev.name,
      status: prev.status || 'success',
    }, targetIndex);
    changed = true;
  });

  if (!changed) return successResult;
  const outputUrls = outputs
    .map((row) => String(row?.outputUrl || row?.url || '').trim())
    .filter(Boolean);
  const urls = outputUrls.length >= (successResult.urls?.length || 0)
    ? outputUrls
    : (Array.isArray(successResult.urls) ? successResult.urls : outputUrls);
  const first = outputs.find((row) => row?.outputUrl || row?.url) || outputs[0];
  const previewUrl = String(first?.outputUrl || first?.url || urls[0] || successResult.previewUrl || '').trim();
  const previewViewUrl = String(first?.viewUrl || first?.displayUrl || successResult.previewViewUrl || '').trim();
  return {
    ...successResult,
    outputs,
    urls: urls.length ? urls : successResult.urls,
    previewUrl,
    previewViewUrl,
    previewDisplayUrl: previewViewUrl || first?.displayUrl || successResult.previewDisplayUrl || '',
  };
}

/**
 * 灵图等外链结果 → ingest-generated 落 OSS → 写回内部 canonical + viewUrl。
 */
export async function finalizeStudioGeneratedResults({
  navId,
  taskId,
  successResult,
  urls,
  outputs,
  onMergeImageSpaceItems,
  idPrefix,
  explicitSource,
} = {}) {
  const toolNavId = String(navId || '').trim();
  if (!toolNavId || !successResult) return successResult;

  const saved = await persistSmartToolResultsToImageSpace({
    navId: toolNavId,
    taskId,
    urls: urls || successResult.urls,
    outputs: outputs || successResult.outputs,
    idPrefix,
    explicitSource,
  });
  mergePersistedImageSpaceItems(saved, onMergeImageSpaceItems);
  return mergeIngestedResultsIntoStudioSuccessResult(successResult, saved, {
    navId: toolNavId,
    taskId,
  });
}

/** img2img 等自定义 outputs 结构：入库并返回内部化后的 outputs / urls */
export async function internalizeStudioOutputEntries({
  navId,
  taskId,
  outputs = [],
  urls = [],
  onMergeImageSpaceItems,
  idPrefix,
  explicitSource,
} = {}) {
  const normalizedOutputs = (outputs || [])
    .map((row, index) => normalizeImg2ImgOutputRow(row, index))
    .filter(Boolean);
  const urlList = (urls?.length ? urls : normalizedOutputs.map((row) => row.outputUrl || row.url))
    .map((u) => String(u || '').trim())
    .filter(Boolean);

  const saved = await persistSmartToolResultsToImageSpace({
    navId,
    taskId,
    urls: urlList,
    outputs: normalizedOutputs,
    idPrefix,
    explicitSource,
  });
  mergePersistedImageSpaceItems(saved, onMergeImageSpaceItems);

  if (!saved.length) {
    return {
      outputs: normalizedOutputs,
      urls: urlList,
      previewUrl: urlList[0] || '',
    };
  }

  const merged = mergeIngestedResultsIntoStudioSuccessResult(
    { outputs: normalizedOutputs, urls: urlList },
    saved,
    { navId, taskId },
  );
  return {
    outputs: merged.outputs || normalizedOutputs,
    urls: merged.urls || urlList,
    previewUrl: merged.previewUrl || urlList[0] || '',
    previewViewUrl: merged.previewViewUrl || '',
    previewDisplayUrl: merged.previewDisplayUrl || '',
  };
}

export async function completeStudioImageTaskUpdate({
  taskId,
  status = 'success',
  successCount,
  failedCount = 0,
  successResult,
  navId,
  onMergeImageSpaceItems,
  idPrefix,
  explicitSource,
}) {
  const finalResult = navId
    ? await finalizeStudioGeneratedResults({
      navId,
      taskId,
      successResult,
      onMergeImageSpaceItems,
      idPrefix,
      explicitSource,
    })
    : successResult;

  await taskAPI.update(taskId, {
    status,
    successCount,
    failedCount,
    finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    previewUrl: finalResult.previewUrl,
    result: finalResult,
  });
  return finalResult;
}
export function buildAiStudioImageOutputRows(urls, rawOutputs = []) {
  return urls.map((url, index) => normalizeImg2ImgOutputRow({
    url,
    index,
    sourceIndex: rawOutputs[index]?.sourceIndex ?? 0,
  }, index)).filter(Boolean);
}

/** 将单条 output 内的 urls[]（如图案裁剪多印花）展开为多行，供入库与结果区展示。 */
export function expandStudioOutputRows(rawOutputs = []) {
  const expanded = [];
  (rawOutputs || []).forEach((row, sourceIndex) => {
    if (!row || row.status === 'failed') return;
    const nestedUrls = Array.isArray(row.urls)
      ? row.urls.map((url) => String(url || '').trim()).filter(Boolean)
      : [];
    const fallbackUrl = String(row.outputUrl || row.url || row.imageUrl || '').trim();
    const rowUrls = nestedUrls.length > 0 ? nestedUrls : (fallbackUrl ? [fallbackUrl] : []);
    rowUrls.forEach((url, printIndex) => {
      const { urls: _nestedUrls, ...rowWithoutNestedUrls } = row;
      expanded.push(normalizeImg2ImgOutputRow({
        ...rowWithoutNestedUrls,
        url,
        outputUrl: url,
        imageUrl: url,
        index: expanded.length,
        sourceIndex: Number.isFinite(Number(row.sourceIndex)) ? Number(row.sourceIndex) : sourceIndex,
        ...(rowUrls.length > 1 ? { printIndex } : {}),
      }, expanded.length));
    });
  });
  return expanded;
}

export function buildAiStudioImageSuccessResult(initialResult, res, urls) {
  const rawOutputs = Array.isArray(res?.outputs) ? res.outputs : [];
  const userPrompt = String(initialResult?.userPrompt ?? initialResult?.prompt ?? '').trim();
  const previewUrl = urls[0] || initialResult.previewUrl || '';
  const outputs = rawOutputs.length > 0
    ? expandStudioOutputRows(rawOutputs)
    : buildAiStudioImageOutputRows(urls, rawOutputs);
  return {
    ...initialResult,
    userPrompt,
    urls,
    outputs,
    previewUrl,
    materialSaved: Boolean(res?.materialSaved),
    materialBatchId: res?.materialBatchId,
    aiInspirationPending: Boolean(res?.aiInspirationPending),
    engine: res?.engine || initialResult.engine || 'lingtu',
    prompt: userPrompt,
    effectivePrompt: String(res?.prompt || initialResult.effectivePrompt || '').trim(),
    jobId: String(res?.jobId || res?.requestId || '').trim(),
    requestId: res?.requestId,
  };
}

export async function createAiStudioImageTask({
  taskType,
  taskName,
  taskId,
  subAccount,
  imageIds = [],
  previewUrl = '',
  initialResult,
  estimatedTotal,
  workspaceMode,
}) {
  const badgeMode = workspaceMode != null && String(workspaceMode).trim() !== ''
    ? workspaceMode
    : getIpeWorkspaceMode();
  await createIpeWorkflowTask({
    id: taskId,
    type: taskType,
    title: `${taskName} ${subAccount}`,
    name: taskName,
    subAccount,
    billingNavId: initialResult?.billingNavId || initialResult?.sourceNav || '',
    imageIds,
    previewUrl,
    status: 'processing',
    total: estimatedTotal,
    successCount: 0,
    failedCount: 0,
    result: initialResult,
  });
  trackPendingTask(taskType, taskId, badgeMode);
}

/**
 * @param {object} [ingestOptions]
 * @param {string} [ingestOptions.navId] 传入则自动将生成图写入图片空间
 * @param {Function} [ingestOptions.onMergeImageSpaceItems]
 */
export async function markAiStudioImageTaskSuccess(
  taskId,
  urls,
  estimatedTotal,
  successResult,
  ingestOptions = null,
) {
  const navId = ingestOptions?.navId;
  const finalResult = navId
    ? await finalizeStudioGeneratedResults({
      navId,
      taskId,
      successResult,
      urls,
      outputs: successResult?.outputs,
      onMergeImageSpaceItems: ingestOptions?.onMergeImageSpaceItems,
      idPrefix: ingestOptions?.idPrefix,
      explicitSource: ingestOptions?.explicitSource,
    })
    : successResult;
  const finalUrls = finalResult.urls || urls;

  await taskAPI.update(taskId, {
    status: 'success',
    successCount: finalUrls.length,
    failedCount: Math.max(0, estimatedTotal - finalUrls.length),
    finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    previewUrl: finalResult.previewUrl,
    result: finalResult,
  });

  return finalResult;
}

function isGenericFailureHint(message) {
  const s = String(message || '').trim();
  return !s || s === '生成失败' || s === '操作失败' || s === '操作失败，请稍后重试' || s === '操作失败，请重试';
}

function pickStoredFailureHint(result) {
  const hint = String(result?.failureHint || result?.error || '').trim();
  if (!isGenericFailureHint(hint)) return hint;
  const outputs = Array.isArray(result?.outputs) ? result.outputs : [];
  const failed = outputs.find((row) => String(row?.error || '').trim());
  const outputError = String(failed?.error || '').trim();
  return isGenericFailureHint(outputError) ? '' : outputError;
}

export async function markAiStudioImageTaskFailed(taskId, initialResult, estimatedTotal, error) {
  const existingHint = pickStoredFailureHint(initialResult);
  const rawHint = String(error?.message || error?.rawMessage || '').trim();
  const preferred = existingHint || (!isGenericFailureHint(rawHint) ? rawHint : '') || '主图创作失败';
  const userMessage = toIpeUserErrorMessage(
    existingHint && isGenericFailureHint(rawHint) ? { message: existingHint } : error,
    preferred,
  );
  if (!error?.ipeIntercepted) {
    logIpeClientError('ai-studio-task', error, userMessage, {
      taskId,
      estimatedTotal,
      rawHint,
      existingHint,
    });
  }
  await taskAPI.update(taskId, {
    status: 'failed',
    failedCount: estimatedTotal,
    finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    result: {
      ...initialResult,
      batchPhase: 'failed',
      failureHint: userMessage,
    },
  });
}
